import { useState } from "react";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Card } from "../lib/types";
import { accentClasses, accentStrong } from "../lib/types";
import { renderMarkdown } from "../lib/markdown";

type Props = {
  card: Card;
  fallbackAccent: keyof typeof accentClasses;
  onClick: () => void;
};

export function CardItem({ card, fallbackAccent, onClick }: Props) {
  const [expanded, setExpanded] = useState(false);
  const accentName = card.accent ?? fallbackAccent;
  const strip = accentStrong[accentName];

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: card.id, data: { type: "card", card } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const hasBody = card.body && card.body.trim().length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={clsx(
        "group relative cursor-grab overflow-hidden rounded-card border border-border bg-surface shadow-card transition-shadow active:cursor-grabbing",
        "hover:shadow-cardHover",
        "before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:content-[''] before:opacity-80",
        strip
      )}
      onClick={(e) => {
        // Only open modal on a plain click (not a drag end).
        if (!isDragging) onClick();
        e.stopPropagation();
      }}
    >
      <div className="px-3.5 py-2.5">
        <div className="flex items-start gap-2">
          <p className="flex-1 text-[13.5px] leading-snug text-ink">
            {card.title}
          </p>
          {hasBody && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              className={clsx(
                "mt-0.5 shrink-0 rounded p-0.5 text-ink-mute transition-all hover:bg-canvas hover:text-ink-soft",
                expanded && "rotate-180"
              )}
              aria-label={expanded ? "collapse" : "expand"}
            >
              <ChevronDown size={13} />
            </button>
          )}
        </div>

        {hasBody && expanded && (
          <div
            /*
             * max-h caps the inline preview at ~200px regardless of how
             * much body text the card has. Beyond that, the preview itself
             * scrolls — keeping the surrounding lane / grid layout stable.
             * Cards with short content still expand only to fit their text.
             *
             * No stopPropagation: a click anywhere here should bubble up
             * and open the edit modal. Links inside linkify stop their
             * own propagation so they still work in isolation.
             */
            className="mt-2 max-h-[200px] overflow-y-auto border-t border-border/60 pt-2 text-[12.5px] leading-relaxed text-ink-soft"
            onWheel={(e) => {
              // When the preview is scrollable, prevent the wheel event from
              // bubbling out to the lane/canvas scroller — otherwise you
              // can't read past line ~10.
              const el = e.currentTarget;
              if (el.scrollHeight > el.clientHeight) e.stopPropagation();
            }}
          >
            {renderMarkdown(card.body)}
          </div>
        )}
      </div>
    </div>
  );
}
